/**
 * Two-Factor Authentication Setup Component
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 * Configuration complète de l'authentification à deux facteurs TOTP
 *
 * Fonctionnalités:
 * - Affichage QR code pour scan
 * - Saisie manuelle du secret
 * - Vérification du code pour activer
 * - Affichage et sauvegarde des backup codes
 * - Désactivation de la 2FA
 * - Régénération des backup codes
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  QrCode,
  Key,
  Copy,
  Check,
  AlertCircle,
  RefreshCw,
  Download,
  Loader2,
  ChevronRight,
  Eye,
  EyeOff,
  AlertTriangle,
} from 'lucide-react';
import { twoFactorApi, TwoFactorStatus, TOTPSetupResult } from '../api/two-factor.api';

export interface TwoFactorSetupProps {
  /** Callback après activation réussie */
  onEnabled?: () => void;
  /** Callback après désactivation */
  onDisabled?: () => void;
  /** Classe CSS additionnelle */
  className?: string;
}

type SetupStep = 'status' | 'qrcode' | 'verify' | 'backup-codes' | 'success' | 'disable';

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  onEnabled,
  onDisabled,
  className = '',
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [setupData, setSetupData] = useState<TOTPSetupResult | null>(null);
  const [currentStep, setCurrentStep] = useState<SetupStep>('status');
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenerateCode, setRegenerateCode] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  // Load 2FA status on mount
  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      const data = await twoFactorApi.getStatus();
      setStatus(data);
    } catch (err: any) {
      setError(t('auth.twoFactorSetup.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await twoFactorApi.setupTOTP();
      setSetupData(data);
      setCurrentStep('qrcode');
    } catch (err: any) {
      setError(err.message || t('auth.twoFactorSetup.setupError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError(t('auth.twoFactorSetup.codeRequired'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await twoFactorApi.verifyTOTPSetup(verificationCode);
      setCurrentStep('backup-codes');
    } catch (err: any) {
      setError(err.message || t('auth.twoFactorSetup.invalidCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      setError(t('auth.twoFactorSetup.codeRequired'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await twoFactorApi.disableTOTP(disableCode);
      setStatus({ ...status!, enabled: false, methods: [] });
      setCurrentStep('status');
      setDisableCode('');
      onDisabled?.();
    } catch (err: any) {
      setError(err.message || t('auth.twoFactorSetup.invalidCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (regenerateCode.length !== 6) {
      setError(t('auth.twoFactorSetup.codeRequired'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const codes = await twoFactorApi.regenerateBackupCodes(regenerateCode);
      setNewBackupCodes(codes);
      setShowRegenerateDialog(false);
      setRegenerateCode('');
      await loadStatus(); // Refresh status
    } catch (err: any) {
      setError(err.message || t('auth.twoFactorSetup.invalidCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyBackupCodes = async () => {
    const codes = newBackupCodes || setupData?.backupCodes || [];
    const text = codes.join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadBackupCodes = () => {
    const codes = newBackupCodes || setupData?.backupCodes || [];
    const text = `FacturXPro - Codes de récupération 2FA\n\nGardez ces codes en lieu sûr. Chaque code ne peut être utilisé qu'une seule fois.\n\n${codes.join('\n')}\n\nDate: ${new Date().toLocaleString('fr-FR')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'facturxpro-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFinishSetup = () => {
    setCurrentStep('status');
    setSetupData(null);
    setVerificationCode('');
    loadStatus();
    onEnabled?.();
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 overflow-hidden`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 transition-all duration-300 outline-none
    text-center text-2xl tracking-[0.5em] font-mono`;

  // Loading state
  if (isLoading && !status) {
    return (
      <div className={`${cardClasses} p-8 flex items-center justify-center ${className}`}>
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        <span className="ml-3 text-gray-600">{t('auth.security.loading')}</span>
      </div>
    );
  }

  // Status Overview
  if (currentStep === 'status') {
    return (
      <div className={`${cardClasses} ${className}`}>
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{t('auth.twoFactorSetup.title')}</h3>
              <p className="text-white/80 text-sm">{t('auth.twoFactorSetup.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50/80 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Status Card */}
          <div className={`rounded-2xl p-5 ${
            status?.enabled
              ? 'bg-green-50/50 border border-green-200'
              : 'bg-amber-50/50 border border-amber-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status?.enabled ? (
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                ) : (
                  <ShieldOff className="w-8 h-8 text-amber-600" />
                )}
                <div>
                  <p className={`font-semibold ${status?.enabled ? 'text-green-700' : 'text-amber-700'}`}>
                    {status?.enabled ? t('auth.twoFactorSetup.enabled') : t('auth.twoFactorSetup.notConfigured')}
                  </p>
                  <p className={`text-sm ${status?.enabled ? 'text-green-600' : 'text-amber-600'}`}>
                    {status?.enabled
                      ? t('auth.twoFactorSetup.enabledDescription')
                      : t('auth.twoFactorSetup.notConfiguredDescription')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Backup codes warning */}
          {status?.enabled && status.backupCodesRemaining !== undefined && status.backupCodesRemaining <= 3 && (
            <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-700">
                  {t('auth.twoFactorSetup.backupCodesLow')}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {t('auth.twoFactorSetup.backupCodesLowText', { count: status.backupCodesRemaining })}
                </p>
              </div>
            </div>
          )}

          {/* Info section */}
          <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-4">
            <h4 className="text-sm font-medium text-teal-700 mb-2">{t('auth.twoFactorSetup.whyEnable')}</h4>
            <ul className="text-xs text-teal-600 space-y-1">
              <li>• {t('auth.twoFactorSetup.reason1')}</li>
              <li>• {t('auth.twoFactorSetup.reason2')}</li>
              <li>• {t('auth.twoFactorSetup.reason3')}</li>
              <li>• {t('auth.twoFactorSetup.reason4')}</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {!status?.enabled ? (
              <button
                onClick={handleStartSetup}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                {t('auth.twoFactorSetup.enable')}
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowRegenerateDialog(true)}
                  className="flex-1 px-4 py-3 bg-white/50 hover:bg-white border border-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('auth.twoFactorSetup.newBackupCodes')}
                </button>
                <button
                  onClick={() => setCurrentStep('disable')}
                  className="px-4 py-3 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <ShieldOff className="w-4 h-4" />
                  {t('auth.twoFactorSetup.disable')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Regenerate Dialog */}
        {showRegenerateDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('auth.twoFactorSetup.regenerateTitle')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('auth.twoFactorSetup.regenerateDescription')}
              </p>
              <input
                type="text"
                maxLength={6}
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ''))}
                className={inputClasses}
                placeholder="000000"
              />
              {error && (
                <p className="text-sm text-red-600 mt-2">{error}</p>
              )}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowRegenerateDialog(false);
                    setRegenerateCode('');
                    setError(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl"
                >
                  {t('auth.changePassword.cancel')}
                </button>
                <button
                  onClick={handleRegenerateBackupCodes}
                  disabled={isLoading || regenerateCode.length !== 6}
                  className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl disabled:opacity-50"
                >
                  {isLoading ? t('auth.twoFactorSetup.regenerating') : t('auth.twoFactorSetup.regenerate')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Backup Codes Display */}
        {newBackupCodes && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <Key className="w-6 h-6 text-teal-600" />
                <h3 className="text-lg font-bold text-gray-900">{t('auth.twoFactorSetup.newBackupCodes')}</h3>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-amber-700">
                  {t('auth.twoFactorSetup.saveCodesNow')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {newBackupCodes.map((code, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-2 text-center font-mono text-sm">
                    {code}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleCopyBackupCodes}
                  className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? t('auth.twoFactorSetup.copied') : t('auth.twoFactorSetup.copy')}
                </button>
                <button
                  onClick={handleDownloadBackupCodes}
                  className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('auth.twoFactorSetup.download')}
                </button>
              </div>
              <button
                onClick={() => setNewBackupCodes(null)}
                className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              >
                {t('auth.twoFactorSetup.savedCodesShort')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // QR Code Step
  if (currentStep === 'qrcode' && setupData) {
    return (
      <div className={`${cardClasses} ${className}`}>
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{t('auth.twoFactorSetup.step1Title')}</h3>
              <p className="text-white/80 text-sm">{t('auth.twoFactorSetup.step1Subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-sm text-gray-600">
            {t('auth.twoFactorSetup.step1DescriptionFull')}
          </p>

          {/* QR Code */}
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              <img src={setupData.qrCode} alt="QR Code 2FA" className="w-48 h-48" />
            </div>
          </div>

          {/* Manual Entry */}
          <div className="bg-gray-50/50 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-2">
              {t('auth.twoFactorSetup.cantScanManual')}
            </p>
            <div className="flex items-center gap-2">
              <code className={`flex-1 px-3 py-2 bg-white rounded-lg text-sm font-mono ${
                showSecret ? '' : 'filter blur-sm select-none'
              }`}>
                {setupData.secret}
              </code>
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(setupData.secret);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            onClick={() => setCurrentStep('verify')}
            className="w-full px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
          >
            {t('auth.twoFactorSetup.continue')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Verify Step
  if (currentStep === 'verify') {
    return (
      <div className={`${cardClasses} ${className}`}>
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{t('auth.twoFactorSetup.step2Title')}</h3>
              <p className="text-white/80 text-sm">{t('auth.twoFactorSetup.step2Subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50/80 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <p className="text-sm text-gray-600">
            {t('auth.twoFactorSetup.step2DescriptionFull')}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.twoFactorSetup.verificationCode')}
            </label>
            <input
              type="text"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value.replace(/\D/g, ''));
                setError(null);
              }}
              className={inputClasses}
              placeholder="000000"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setCurrentStep('qrcode')}
              className="flex-1 px-4 py-3 bg-white/50 hover:bg-white border border-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-all duration-300"
            >
              {t('auth.twoFactorSetup.back')}
            </button>
            <button
              onClick={handleVerifyCode}
              disabled={isLoading || verificationCode.length !== 6}
              className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-sm transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {t('auth.twoFactorSetup.verify')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Backup Codes Step
  if (currentStep === 'backup-codes' && setupData) {
    return (
      <div className={`${cardClasses} ${className}`}>
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{t('auth.twoFactorSetup.step3Title')}</h3>
              <p className="text-white/80 text-sm">{t('auth.twoFactorSetup.step3Subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-700">{t('auth.twoFactorSetup.backupImportant')}</p>
              <p className="text-xs text-amber-600 mt-1">
                {t('auth.twoFactorSetup.backupImportantText')}
              </p>
            </div>
          </div>

          {/* Backup Codes Grid */}
          <div className="bg-gray-50/50 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-2">
              {setupData.backupCodes.map((code, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg p-3 text-center font-mono text-sm border border-gray-200"
                >
                  {code}
                </div>
              ))}
            </div>
          </div>

          {/* Copy/Download Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleCopyBackupCodes}
              className="flex-1 px-4 py-3 bg-white/50 hover:bg-white border border-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? t('auth.twoFactorSetup.copied') : t('auth.twoFactorSetup.copyCodes')}
            </button>
            <button
              onClick={handleDownloadBackupCodes}
              className="flex-1 px-4 py-3 bg-white/50 hover:bg-white border border-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {t('auth.twoFactorSetup.downloadTxt')}
            </button>
          </div>

          <button
            onClick={handleFinishSetup}
            className="w-full px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" />
            {t('auth.twoFactorSetup.savedCodes')}
          </button>
        </div>
      </div>
    );
  }

  // Disable Step
  if (currentStep === 'disable') {
    return (
      <div className={`${cardClasses} ${className}`}>
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <ShieldOff className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{t('auth.twoFactorSetup.disableTitle')}</h3>
              <p className="text-white/80 text-sm">{t('auth.twoFactorSetup.disableSubtitle')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50/80 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-700">
              {t('auth.twoFactorSetup.disableWarningFull')}
            </p>
          </div>

          <p className="text-sm text-gray-600">
            {t('auth.twoFactorSetup.disableConfirmText')}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.twoFactorSetup.confirmationCode')}
            </label>
            <input
              type="text"
              maxLength={6}
              value={disableCode}
              onChange={(e) => {
                setDisableCode(e.target.value.replace(/\D/g, ''));
                setError(null);
              }}
              className={inputClasses}
              placeholder="000000"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setCurrentStep('status');
                setDisableCode('');
                setError(null);
              }}
              className="flex-1 px-4 py-3 bg-white/50 hover:bg-white border border-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-all duration-300"
            >
              {t('auth.changePassword.cancel')}
            </button>
            <button
              onClick={handleDisable2FA}
              disabled={isLoading || disableCode.length !== 6}
              className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium text-sm transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldOff className="w-4 h-4" />
              )}
              {t('auth.twoFactorSetup.disableButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TwoFactorSetup;
